import React, { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { UserContext, AccessTokenContext } from '../../context/context.js';
import { fetchPatients, updatePatientsGroup } from "../../App/Features/Patient_Scheduling/Patients_Thunks.js";
import { selectPatientId, toggleGrouping } from '../../App/Features/Patient_Scheduling/Patients_Slice.js';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import Loading from "../../pop-ups/loading";
import FloatingModal from "../../static/Modals/Floating_Modal";
import CreatePatient from "../../Modal_Components/Create_Patient";

import styles from "./Patient_Schedule_List.module.css";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';

const PatientScheduleList = ({ handleDragStart, myEvents, start, end }) => {
  const user = useContext(UserContext);
  const accessToken = useContext(AccessTokenContext);
  const dispatch = useDispatch();
  const { list, status, isGroupingOn, selectedPatientId } = useSelector((state) => state.patients);

  // const [remainingPatients, setRemainingPatients]=useState([]);
  const [activePopoverId, setActivePopoverId] = useState(null);
  const [showNewPatientModal, setShowNewPatientModal] = useState(false);
  const [isEditingGroup, setIsEditingGroup] = useState(false);
  const [groupingList, setGroupingList] = useState([]);

  const clusterMap = {
    0: '#6a0dad',  // Dark Purple
    1: '#c2185b',  // Deep Pink
    2: '#0077b6',  // Rich Blue
    3: '#d97706',  // Golden Orange
    4: '#1b5e20',  // Dark Green
    5: '#b71c1c',  // Deep Crimson
    6: '#0c5460',  // Teal Blue
    7: '#4e342e',  // Dark Brown
  };

  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchPatients({ user, accessToken }));
    }
  }, [status, user.authId, accessToken, dispatch]);

  useEffect(() => {
    if(selectedPatientId !== '' && selectedPatientId !== null) {
      setActivePopoverId(selectedPatientId);
    }
  }, [selectedPatientId]);

  // Replace your current groupingList useEffect with this one
  useEffect(() => {
    if (!list) {
      setGroupingList([]);
      return;
    }

    // Group patients by their current group number
    const groupedObject = list.reduce((acc, patient) => {
      (acc[patient.group] ||= []).push(patient);
      return acc;
    }, {});

    // Filter out empty groups and create array of [groupNumber, patients] pairs
    const filteredGroups = Object.entries(groupedObject).filter(([_, patients]) => patients.length > 0);
    
    // Sort by original group number
    filteredGroups.sort((a, b) => parseInt(a[0]) - parseInt(b[0]));
    
    // Create new patients list with normalized group numbers
    const updatedPatients = [];
    const newGroupingList = [];
    
    // Create new sequential groupingList and update patient group numbers
    filteredGroups.forEach(([originalGroup, patients], newIndex) => {
      // Create new entry for groupingList with sequential index
      newGroupingList.push([newIndex.toString(), patients]);
      
      // Add patients with updated group numbers to updatedPatients array
      patients.forEach(patient => {
        updatedPatients.push({
          ...patient,
          group: newIndex // Update to sequential group number
        });
      });
    });
    
    // Update the groupingList with sequential numbering
    setGroupingList(newGroupingList);
    
    // Only dispatch update if group numbers have actually changed
    const hasGroupNumbersChanged = updatedPatients.some(
      updatedPatient => {
        const originalPatient = list.find(p => p._id === updatedPatient._id);
        return originalPatient && parseInt(originalPatient.group) !== parseInt(updatedPatient.group);
      }
    );
    
    if (hasGroupNumbersChanged) {
      dispatch(updatePatientsGroup({ user, accessToken, patients: updatedPatients }));
    }
  }, [list, user, accessToken, dispatch]);

  const handleTogglePopover = (patientId) => {
    setActivePopoverId((prevId) => (prevId === patientId ? null : patientId));
    dispatch(selectPatientId(patientId));
  };

  const handleCloseModal = async () => {
    dispatch(fetchPatients({ user, accessToken }));
    setShowNewPatientModal(false);
  }

  const handleAddPatient = () => setShowNewPatientModal(true);

 const handleEditGroupToggle = () => setIsEditingGroup(!isEditingGroup)
 
  const handleGroupingToggle = () => {
    dispatch(toggleGrouping());
  }

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
  
    const { source, destination } = result;
  
    // Find the source & destination groups
    const sourceGroupIndex = parseInt(source.droppableId); // Group index from droppableId
    const destGroupIndex = parseInt(destination.droppableId);
  
    // Clone the groupingList (shallow copy)
    const updatedGroups = [...groupingList];
  
    // Extract patient lists from the groups
    const sourceGroup = updatedGroups[sourceGroupIndex];
    const destGroup = updatedGroups[destGroupIndex];
  
    // Clone the patient arrays to avoid mutating state directly
    const sourcePatients = [...sourceGroup[1]];
    const destPatients = [...destGroup[1]];
  
    // Remove dragged patient from source group
    const [movedPatient] = sourcePatients.splice(source.index, 1);
  
    // Add patient to destination group
    destPatients.splice(destination.index, 0, movedPatient);
  
    // Update groups with modified patient lists
    updatedGroups[sourceGroupIndex] = [sourceGroup[0], sourcePatients];
    updatedGroups[destGroupIndex] = [destGroup[0], destPatients];
  
    setGroupingList(updatedGroups);
    handleChangeGroupNumber(result.draggableId, destGroupIndex);
  };

  const handleChangeGroupNumber = (id, groupIndex) => {
    const updatedList = list.map((p) => 
      p._id === id ? { ...p, group: groupIndex } : p
    );
  
    dispatch(updatePatientsGroup({ user, accessToken, patients: updatedList }));
  };
  
  const popover = (patient) => (
    <Popover id={`${styles.popoverBasic}`}>
      <Popover.Header className={styles.popoverHeader} as="h3">
        {patient.firstName} {patient.lastName}
      </Popover.Header>
      <Popover.Body>
        <div>
          <div className={`${patient.primaryNumber === "" ? "d-none" : "d-flex"} d-flex flex-column mb-2`}>
            <div className={`${styles.label} d-flex`}>Primary:</div>
            <div className={`${styles.infoDiv} d-flex flex-wrap`}>
              {patient.primaryNumber || "--"}
            </div>
          </div>
          <div className="d-flex flex-column mb-2">
            <div className={`${styles.label} d-flex`}>Address:</div>
            <div className={`${styles.infoDiv} d-flex flex-wrap`}>
              {patient.address}
            </div>
          </div>
          <div className="d-flex flex-column mb-2">
            <div className={`${styles.label} d-flex`}>Frequency:</div>
            <div className={`${styles.infoDiv} d-flex flex-wrap`}>
              {patient.frequency}/Week
            </div>
          </div>
          <div className={`${Object.values(patient.noSeeDays).some(value => value === true) ? "d-flex" : "d-none"} d-flex flex-column mb-2`}>
            <div className={`${styles.label} d-flex`}>Cannot Be Seen:</div>
            <div className="d-flex flex-wrap align-items-center">
              <div className="d-flex flex-wrap">
                {patient.noSeeDays &&
                  Object.entries(patient.noSeeDays).map(
                    ([propertyName, propertyValue]) =>
                      propertyValue ? (
                        <div
                          className="d-flex me-1 mb-1 badge"
                          id="customBadge"
                          key={propertyName}
                        >
                          {propertyName}
                        </div>
                      ) : null
                  )}
              </div>
            </div>
          </div>
        </div>
      </Popover.Body>
    </Popover>
  );

  const header = () => {
    return (
      <div className="d-flex align-items-center mb-2 justify-content-between w-100">
        <button onClick={handleAddPatient} className={`${list.length > 0 ? "" : `${styles.pulsingText}`} transparentBtnSecondary`}>Add Patient</button>
        <div className="d-flex">
          <label className="switch">
            <input type="checkbox" checked={isGroupingOn} onChange={handleGroupingToggle} />
            <span className="slider"></span>
          </label>
          <div className="switch-label">Auto-Group</div>
        </div>
        
      </div>
    );
  }

  if (status === 'loading') return <div><Loading /></div>;

  if (status === 'failed') {
    return (
      <div className="d-flex flex-column justify-content-center align-items center">
        <div className="d-flex justify-content-center align-items center"><img src="/assets/images/Scheduling.png" alt="Notes" width={200} height={200} /></div>
        <div className={`${styles.noPatientDiv}`}>Add a few patients to get started. From there it's easy; Drag, Drop & Test!</div>
        <button onClick={handleAddPatient} className="transparentBtnSecondary my-2">Add Patient</button>
        <FloatingModal 
          title="New Patient" 
          component={<CreatePatient userId={user.authId} accessToken={accessToken} handleClose={handleCloseModal}  />} 
          show={showNewPatientModal} 
          handleClose={handleCloseModal}
        />
      </div>
    );
  }

  if(isGroupingOn) {
    if(isEditingGroup) {
      return (
        <div className={styles.editingGroups}>
          <div className="d-flex flex-column justify-content-start align-items-start">
            {header()}
            <button onClick={handleEditGroupToggle} className="secondaryBtnSmall mt-2">Done</button>
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <ul className="d-flex flex-wrap">
                {groupingList.map(([group, patients], index) => (
                  <Droppable key={group} droppableId={`${index}.5`}>
                    {(provided) => (
                      <li
                        className="list-group-item m-1"
                        style={{ outline: `2px solid ${clusterMap[group]}`, borderRadius: "8px", padding: "5px" }}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        <ul className="list-group">
                          {patients.map((patient, index) => (
                            <Draggable key={String(patient._id)} draggableId={String(patient._id)} index={index}>
                              {(provided) => (
                                <li
                                  className={`${styles.patientCard} d-flex justify-content-center align-items-center my-1`}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    background: clusterMap[group],
                                    border: `1px solid ${clusterMap[group]}`,
                                    ...provided.draggableProps.style,
                                  }}
                                >
                                  <OverlayTrigger
                                    trigger="click"
                                    placement="top"
                                    show={activePopoverId === patient._id}
                                    onToggle={() => handleTogglePopover(patient._id)}
                                    overlay={popover(patient)}
                                  >
                                    <span>
                                      {patient.firstName} {patient.lastName}
                                    </span>
                                  </OverlayTrigger>
                                </li>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </ul>
                      </li>
                    )}
                  </Droppable>
                ))}
              </ul>
            </DragDropContext>
            <FloatingModal
              title="New Patient"
              component={<CreatePatient userId={user.authId} accessToken={accessToken} handleClose={handleCloseModal} />}
              show={showNewPatientModal}
              handleClose={handleCloseModal}
            />
          </div>
        </div>
      );      
    } 
     
    return (
      <div style={{ padding: "3px" }}>
        <div className="d-flex flex-column justify-content-start align-items-start">
          {header()}
          <button onClick={handleEditGroupToggle} className="secondaryBtnSmall mt-2">Edit</button>
          <ul className="d-flex flex-wrap">
            {groupingList.map(([group, patients], index) => (
              <li
                key={group}
                className="list-group-item m-1"
                style={{ border: `1px solid ${clusterMap[group]}`, borderRadius: "8px", padding: "5px"}}
              >
                <ul className="list-group">
                  {patients.map((patient, index) => (
                    <li key={patient._id} className="d-flex justify-content-start align-items-start my-1">
                      <OverlayTrigger
                        trigger="click"
                        placement="top"
                        show={activePopoverId === patient._id}
                        onToggle={() => handleTogglePopover(patient._id)}
                        overlay={popover(patient)}
                      >
                        <button
                          draggable 
                          onDragStart={() => handleDragStart(`${patient.firstName} ${patient.lastName}`, patient.address, patient.coordinates, patient.additional ? null : undefined, patient.additional, null)} 
                          className={`${styles.patientCard} d-flex justify-content-center align-items-center`}
                          style={{ background: `${clusterMap[group]}`, border: `1px solid ${clusterMap[group]}` }}
                        >
                          {patient.firstName} {patient.lastName}
                        </button>
                      </OverlayTrigger>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          <FloatingModal
            title="New Patient"
            component={<CreatePatient userId={user.authId} accessToken={accessToken} handleClose={handleCloseModal} />}
            show={showNewPatientModal}
            handleClose={handleCloseModal}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column justify-content-start align-items-start">
     {header()}
      <div className="d-flex justify-content-start align-items-start w-100 flex-wrap">
          {list.map((patient) => 
            (   
              <div key={patient._id} className="d-flex justify-content-center align-items-center m-1">
                <OverlayTrigger trigger="click"
                  placement="top"
                  show={activePopoverId === patient._id}
                  onToggle={() => handleTogglePopover(patient._id)}
                  overlay={popover(patient)}
                >
                  <button 
                    draggable 
                    onDragStart={() => handleDragStart(`${patient.firstName} ${patient.lastName}`, patient.address, patient.coordinates, patient.additional ? null : undefined, patient.additional, null)} 
                    className={`${styles.patientCard} d-flex justify-content-center align-items-center`}
                  >
                    {patient.firstName} {patient.lastName}
                  </button>
                </OverlayTrigger>
              </div>
            ) 
          )}
      </div>
      <FloatingModal 
        title="New Patient" 
        component={<CreatePatient userId={user.authId} accessToken={accessToken} handleClose={handleCloseModal}  />} 
        show={showNewPatientModal} 
        handleClose={handleCloseModal}
      />
    </div>    
  );
}

export default PatientScheduleList

  